import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import animate from '@play-co/timestep-core/lib/animate';
import { animDuration, isPlatformGuest } from 'src/lib/utils';
import { ImageView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import MenuActionButton, { MenuActionType } from './MenuActionButton';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { openProfile } from '../profile/profile.sequence';
import StateObserver, { user } from 'src/StateObserver';
import features from 'src/replicant/ruleset/features';
import { GCInstant as platform } from 'src/lib/gcinstant';
import {
  isDecorationTutorialInProgress,
  isDecorationUnlocked,
} from '../decoration/helpers';
import TutorialHand from './shared/TutorialHand';
import { openCatalogPopup } from '../catalog/catalog.sequence';
import { promptLINELoginNew } from '../guest/guest.sequence';
import { getCatalogItems } from '../catalog/helpers';
import { openSettingsPopup } from '../settings/settings.sequence';

const ui = uiConfig.buruburu.menu;

export default class MainMenu extends FlexBoxImageScaleView {
  // Tutorial hand
  private hand: TutorialHand;

  constructor() {
    super(ui.menu);

    // Tutorial hand
    this.hand = new TutorialHand({});

    this.image.onInputStart = () => this.emit('Close');

    const firstRow = this.addSubview(new FlexBoxView(ui.menuRow));
    const secondRow = this.addSubview(new FlexBoxView(ui.menuRow));

    this.createMenu(firstRow, secondRow);

    const closeHint = this.addSubview(new ImageView(ui.closeHintBg));
    closeHint.canHandleEvents(false, true);
    getFlexNode(closeHint).updateStyle({ marginTop: 23 });
    closeHint.addSubview(
      new LangBitmapFontTextView({
        ...ui.closeHintText,
        localeText: () => i18n('footer.tapToClose'),
        width: closeHint.style.width,
        height: closeHint.style.height,
      }),
    );
  }

  private createMenu(firstRow: FlexBoxView, secondRow: FlexBoxView) {
    const { onSelect } = this;

    firstRow.addSubview(
      new MenuActionButton({
        type: 'catalog',
        onSelect,
        isDisabled: !getCatalogItems().length,
      }),
    );
    firstRow.addSubview(
      new MenuActionButton({ type: 'encyclopedia', onSelect }),
    );
    firstRow.addSubview(
      new MenuActionButton({
        type: 'profile',
        onSelect,
        isDisabled:
          !isPlatformGuest(platform) && !features(user()).ingameProfile,
      }),
    );

    secondRow.addSubview(
      new MenuActionButton({
        type: 'decor',
        onSelect,
        isDisabled: !isDecorationUnlocked(),
      }),
    );
    secondRow.addSubview(
      new MenuActionButton({
        type: 'familyTree',
        onSelect,
      }),
    );
    secondRow.addSubview(new MenuActionButton({ type: 'settings', onSelect }));
  }

  public fadeIn(superview: View) {
    // Show tutorial hand
    this.tutorialShow();

    animate(this)
      .clear()
      .now(() =>
        this.updateOpts({
          superview,
          visible: true,
          opacity: 0,
          offsetY: 100,
        }),
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  public fadeOut() {
    // Hide tutorial hand
    this.tutorialHide();

    animate(this)
      .clear()
      .then({ opacity: 0, offsetY: 100 }, animDuration, animate.easeOut)
      .then(() => {
        this.removeFromSuperview();
      });
  }

  private onSelect = async (type: MenuActionType) => {
    sounds.sfx.play('ok');
    switch (type) {
      case 'settings': {
        // We need this to make switching between decoration editor work smoothly
        if (user().allowDecorationEditor) {
          this.emit('Close');
        }

        await openSettingsPopup('hud');
        return;
      }
      case 'profile': {
        if (isPlatformGuest(platform)) {
          await promptLINELoginNew('profile');
          return;
        }

        await openProfile('hud');
        return;
      }
      case 'petCollection':
      case 'encyclopedia': {
        await openPopupPromise('popupEncyclopedia', { origin: 'hud' });
        return;
      }

      case 'decor': {
        this.emit('Close');

        await openPopupPromise('popupSelectDecorationRoom', { origin: 'hud' });
        return;
      }

      case 'familyTree': {
        if (await openPopupPromise('popupFamilyTree', { origin: 'hud' })) {
          this.emit('Close');
        }
        return;
      }
      case 'catalog': {
        await openCatalogPopup('hud');
        return;
      }
    }

    this.emit('Close');
  };

  /**
   * Show tutorial hand
   */
  private tutorialShow() {
    const { decoration } = StateObserver.getState();

    if (
      isDecorationTutorialInProgress() &&
      decoration.tutorialDecorationScreenWasOpened === false &&
      decoration.tutorialDialogWasShown === true
    ) {
      this.addSubview(this.hand);
      this.hand.fadeIn(-185, -140, false);
    }
  }

  /**
   * Hide tutorial hand
   */
  private tutorialHide() {
    if (this.hand.getSuperview()) {
      this.hand.fadeOut();
      this.removeSubview(this.hand);
    }
  }
}
